

import { CheckCircle2, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fmtPts } from './utils';

/**
 * Card-league weekly lineup panel: the scored 8 (slot chip + line-art avatar +
 * points) with the 2 bench rows dimmed below, headed by a prominent
 * "Provisional — finalizes Wed" vs "Official ✓" badge. Best Ball shows the
 * auto-derived optimal 8 ("optimal pts"); DRAFT_PPR shows the frozen chosen 8
 * ("your pts") plus an optional benchmark line. In preview mode (week not yet
 * scored) the same panel shows the lineup without points.
 */

export interface RosterWeekPlayer {
  cardId: string;
  playerId: string;
  playerName: string;
  position: string;
  team?: string | null;
  imageUrl: string | null;
  /** Starter slot ("QB"…"FLEX") or null/"BENCH" for the two reserves. */
  slot: string | null;
  /** Weekly points (absent in preview mode). */
  points?: number;
}

interface RosterWeekPanelProps {
  players: RosterWeekPlayer[];
  optimalPoints?: number;
  benchPoints?: number;
  isOfficial?: boolean;
  /** 'scored' = weekly-scores rows with points; 'preview' = roster, no points yet. */
  mode: 'scored' | 'preview';
  /** Headline points label — "optimal pts" (Best Ball) vs "your pts" (DRAFT_PPR). */
  pointsLabel?: string;
  /**
   * DRAFT_PPR only: what the optimal 8 would have scored — renders an
   * "Optimal: X · Y left on bench" line under the headline.
   */
  benchmarkPoints?: number;
}

// Server slots are position-level (NflRosterSlot): RB/WR repeat for the two
// starters of that position; BENCH/null marks the two reserves.
const SLOT_STYLE: Record<string, { color: string; bg: string }> = {
  QB: { color: 'text-[#DC2626]', bg: 'bg-[#DC2626]/15' },
  RB: { color: 'text-emerald-400', bg: 'bg-emerald-500/15' },
  WR: { color: 'text-[#3B82F6]', bg: 'bg-[#3B82F6]/15' },
  TE: { color: 'text-orange-400', bg: 'bg-orange-500/15' },
  FLEX: { color: 'text-gold', bg: 'bg-gold/15' },
  K: { color: 'text-indigo-400', bg: 'bg-indigo-500/15' },
  BN: { color: 'text-warm-gray', bg: 'bg-zinc-500/15' },
};

const SLOT_ORDER = ['QB', 'RB', 'WR', 'TE', 'FLEX', 'K'];

function isBench(p: RosterWeekPlayer): boolean {
  return p.slot == null || p.slot === 'BENCH';
}

function PlayerRow({
  player,
  bench,
  showPoints,
}: {
  player: RosterWeekPlayer;
  bench: boolean;
  showPoints: boolean;
}) {
  const slotLabel = bench ? 'BN' : player.slot!;
  const style = SLOT_STYLE[slotLabel] ?? SLOT_STYLE.BN;

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-2',
        bench && 'opacity-50',
      )}
    >
      {/* Slot chip */}
      <span
        className={cn(
          'w-11 shrink-0 text-center px-1.5 py-1 rounded text-[10px] font-bold tracking-wider',
          style.bg,
          style.color,
        )}
      >
        {slotLabel}
      </span>

      {/* Line-art avatar (no sepia — NFL art renders untouched) */}
      <span className="w-9 h-9 rounded-full bg-parchment border border-parchment-border overflow-hidden flex items-center justify-center shrink-0">
        {player.imageUrl ? (
          <img
            src={player.imageUrl}
            alt=""
            className="w-full h-full object-cover object-top"
          />
        ) : (
          <span className="text-xs font-serif font-bold text-surface/50">
            {player.playerName.split(' ').map((n) => n[0]).join('')}
          </span>
        )}
      </span>

      {/* Name + meta */}
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-cream truncate">
          {player.playerName}
        </span>
        <span className="block text-[11px] text-tertiary truncate">
          {player.position}
          {player.team ? ` · ${player.team}` : ''}
        </span>
      </span>

      {/* Points */}
      {showPoints && (
        <span
          className={cn(
            'text-sm font-bold tabular-nums shrink-0',
            bench ? 'text-warm-gray' : 'text-gold',
          )}
        >
          {fmtPts(player.points ?? 0)}
        </span>
      )}
    </div>
  );
}

export function RosterWeekPanel({
  players,
  optimalPoints,
  benchPoints,
  isOfficial = false,
  mode,
  pointsLabel = 'optimal pts',
  benchmarkPoints,
}: RosterWeekPanelProps) {
  const starters = players
    .filter((p) => !isBench(p))
    .sort(
      (a, b) => SLOT_ORDER.indexOf(a.slot ?? '') - SLOT_ORDER.indexOf(b.slot ?? ''),
    );
  const bench = players.filter(isBench);
  const scored = mode === 'scored';

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-2 overflow-hidden max-w-md">
      {/* Header — points headline + official/provisional badge */}
      <div className="px-4 py-3 border-b border-surface-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.2em] text-tertiary font-bold">
            Your Lineup
          </p>
          {scored && optimalPoints != null && (
            <p className="text-xl font-serif font-bold text-gold leading-tight">
              {fmtPts(optimalPoints)}{' '}
              <span className="text-xs font-sans font-medium text-warm-gray">
                {pointsLabel}
              </span>
            </p>
          )}
          {/* DRAFT_PPR benchmark — "you scored X, optimal was Y" retention hook */}
          {scored && optimalPoints != null && benchmarkPoints != null && (
            <p className="text-[11px] text-warm-gray leading-tight mt-0.5">
              Optimal: <span className="font-semibold text-cream">{fmtPts(benchmarkPoints)}</span>
              {benchmarkPoints - optimalPoints > 0.004 && (
                <span className="text-orange-400">
                  {' '}· {fmtPts(Math.round((benchmarkPoints - optimalPoints) * 100) / 100)} left on bench
                </span>
              )}
            </p>
          )}
        </div>
        {scored ? (
          isOfficial ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-verdigris/10 border border-verdigris/30 text-verdigris text-xs font-bold shrink-0">
              <CheckCircle2 className="h-3.5 w-3.5" /> Official
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs font-bold shrink-0">
              <Clock className="h-3.5 w-3.5" /> Provisional — finalizes Wed
            </span>
          )
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-surface-3 border border-surface-4 text-warm-gray text-xs font-medium shrink-0">
            Points appear as games go final
          </span>
        )}
      </div>

      {/* Optimal 8 */}
      <div className="divide-y divide-surface-3/60">
        {starters.map((p) => (
          <PlayerRow key={p.cardId} player={p} bench={false} showPoints={scored} />
        ))}
      </div>

      {/* Bench 2 — dimmed */}
      {bench.length > 0 && (
        <div className="border-t border-surface-3">
          <p className="px-4 pt-2 text-[10px] uppercase tracking-[0.2em] text-tertiary/70 font-bold">
            Bench{scored && benchPoints != null ? ` · ${fmtPts(benchPoints)} pts` : ''}
          </p>
          <div className="divide-y divide-surface-3/40 pb-1">
            {bench.map((p) => (
              <PlayerRow key={p.cardId} player={p} bench showPoints={scored} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
